import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { performanceDefaultRange } from '../reportDateDefaults';
import { maxReportDate } from '../lib/reportDates';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled, prefilledRange } from '../lib/reportPrefill';
import IrrMethodField, { DEFAULT_IRR_METHOD } from './IrrMethodField';
import MaskedDateField from '../../../components/ui/MaskedDateField';
import BenchmarkIndexSelect from './shared/BenchmarkIndexSelect';
import { loadBenchmarkGroups, saveBenchmarkGroups } from '../lib/benchmarkIndexPref';

/**
 * Portfolio Performance Summary Confirmation Popup.
 *
 * Mirrors the Banyan "Portfolio Performance Summary" printed report
 * (Adobe Scan May 20, 2026.pdf page 2). Collects:
 *   - Portfolio (codename, group with @ prefix, or client)
 *   - Date range: From <start> Through <end>
 *   - Printed header inputs (firm name, account name, account code)
 *
 * The portfolio/date inputs drive the Athena query; the header inputs are
 * just printed verbatim at the top of the rendered report.
 */
const PortfolioPerformanceSummaryPopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [firmName, setFirmName] = useState('Banyan Tree Asset Management, LLC');
  const [accountName, setAccountName] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [irrMethod, setIrrMethod] = useState(DEFAULT_IRR_METHOD);
  const [selectedIndexGroups, setSelectedIndexGroups] = useState([]);

  // Default performance range (year-end..year-end) that seeds the date inputs.
  const defaultRange = performanceDefaultRange();

  useEffect(() => {
    if (isOpen) {
      setSelection({ selectedItem: null, params: {} });
      setIrrMethod(prefilled(initialParams, 'irr_method', DEFAULT_IRR_METHOD));
      setFirmName(prefilled(initialParams, 'firm_name', 'Banyan Tree Asset Management, LLC'));
      setAccountName(prefilled(initialParams, 'account_name', ''));
      setAccountCode(prefilled(initialParams, 'account_code', ''));
      // Performance report: default to year-end..year-end (PM Q18 convention,
      // e.g. 2024-12-31..2025-12-31; matches the PDF baseline).
      const range = prefilledRange(initialParams, { start: defaultRange.start, end: defaultRange.end });
      setStartDate(range.start);
      setEndDate(range.end);
      setSelectedIndexGroups(prefilled(initialParams, 'selected_index_groups', loadBenchmarkGroups()));
    }
  }, [isOpen, defaultRange.start, defaultRange.end, initialParams]);

  // The single thin-params builder used by handleConfirm (incl. the
  // account_code fallback).
  const buildParams = (sel) => {
    const params = {
      date: endDate,
      date_range: { start: startDate, end: endDate },
      firm_name: firmName,
      account_name: accountName,
      account_code: accountCode,
      selected_index_groups: selectedIndexGroups,
      ...(irrMethod !== DEFAULT_IRR_METHOD ? { irr_method: irrMethod } : {}),
      ...sel.params,
    };

    if (!params.account_code) {
      params.account_code = params.portfolio || accountCode;
    }

    return params;
  };

  const validate = (sel = selection) => {
    const e = {};
    if (!sel.selectedItem) e.portfolio = 'Portfolio is required';
    if (!startDate) e.startDate = 'Start date is required';
    if (!endDate) e.endDate = 'End date is required';
    if (startDate && endDate && startDate > endDate) e.endDate = 'End date must be on or after start date';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleConfirm = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validate(sel)) return;
    setIsLoading(true);
    try {
      saveBenchmarkGroups(selectedIndexGroups);
      await onConfirm(buildParams(sel));
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl ${darkMode ? 'bg-[#141414]' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {reportInfo?.name || 'Portfolio Performance Summary'}
          </h3>
          <button onClick={onClose} className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {reportInfo?.description && (
          <div className={`mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {reportInfo.description}
          </div>
        )}

        <div className="space-y-4">
          {/* Portfolio */}
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleConfirm}  disabled={isLoading}/>
            {errors.portfolio && <p className="text-red-500 text-xs mt-1">{errors.portfolio}</p>}
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                From <span className="text-red-500">*</span>
              </label>
              <MaskedDateField
                value={startDate}
                max={maxReportDate()}
                onChange={(e) => setStartDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                } ${errors.startDate ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Through <span className="text-red-500">*</span>
              </label>
              <MaskedDateField
                value={endDate}
                max={maxReportDate()}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                } ${errors.endDate ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
            </div>
          </div>

          <IrrMethodField
            value={irrMethod}
            onChange={setIrrMethod}
            darkMode={darkMode}
            disabled={isLoading}
          />

          {/* Printed report-header inputs */}
          <div className="grid grid-cols-1 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Firm Name (printed at top of report)
              </label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Account Name
                </label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. REDACTED-CLIENT"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Account Code
                </label>
                <input
                  type="text"
                  value={accountCode}
                  onChange={(e) => setAccountCode(e.target.value)}
                  placeholder="e.g. EQY002536"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          <BenchmarkIndexSelect
            value={selectedIndexGroups}
            onChange={setSelectedIndexGroups}
            darkMode={darkMode}
          />

          {errors.submit && (
            <div className={`p-3 rounded-lg border ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{errors.submit}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel || onClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300 hover:bg-[#1a1a1a]' : ''}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Generating...
              </>
            ) : (
              'OK'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPerformanceSummaryPopup;
